import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { movieList } from "@/mocks/movies";
import { communityList } from "@/mocks/community";

interface Suggestion {
  key: string;
  type: "news" | "movie" | "post";
  id: string;
  title: string;
  meta: string;
  path: string;
}

interface SearchBoxProps {
  className?: string;
  inputId?: string;
  onNavigate?: () => void;
}

const typeLabel: Record<Suggestion["type"], string> = {
  news: "资讯",
  movie: "影视",
  post: "社区",
};

export default function SearchBox({ className = "", inputId = "site-search", onNavigate }: SearchBoxProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const index = useMemo<Suggestion[]>(() => {
    const news: Suggestion[] = newsList.map((n) => ({
      key: `news-${n.id}`,
      type: "news",
      id: n.id,
      title: n.title,
      meta: `${n.category} · ${n.date}`,
      path: `/news/detail/${n.id}`,
    }));
    const movies: Suggestion[] = movieList.map((m) => ({
      key: `movie-${m.id}`,
      type: "movie",
      id: m.id,
      title: m.title,
      meta: `${m.region} · ${m.year} · ${m.rating.toFixed(1)}分`,
      path: `/movies/detail/${m.id}`,
    }));
    const posts: Suggestion[] = communityList.map((p) => ({
      key: `post-${p.id}`,
      type: "post",
      id: p.id,
      title: p.title,
      meta: `${p.tag} · ${p.author}`,
      path: `/community/detail/${p.id}`,
    }));
    return [...news, ...movies, ...posts];
  }, []);

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return index
      .filter((item) => item.title.toLowerCase().includes(kw) || item.meta.toLowerCase().includes(kw))
      .slice(0, 8);
  }, [keyword, index]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const go = (path: string) => {
    setKeyword("");
    setOpen(false);
    if (onNavigate) onNavigate();
    navigate(path);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (results.length > 0) {
      go(results[0].path);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} role="search">
        <div className="flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors">
          <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
          <input
            id={inputId}
            type="text"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="搜索资讯 / 影视 / 帖子"
            aria-label="站内搜索"
            autoComplete="off"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              aria-label="清空搜索"
              className="flex h-5 w-5 items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-line text-sm" aria-hidden="true"></i>
            </button>
          )}
        </div>
      </form>

      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2">
          {results.length > 0 ? (
            <ul className="flex flex-col">
              {results.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => go(item.path)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-primary-50 cursor-pointer"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i
                        className={
                          item.type === "news"
                            ? "ri-newspaper-line text-sm"
                            : item.type === "movie"
                              ? "ri-movie-2-line text-sm"
                              : "ri-chat-smile-2-line text-sm"
                        }
                        aria-hidden="true"
                      ></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900">{item.title}</span>
                      <span className="block truncate text-xs text-foreground-500">{item.meta}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
                      {typeLabel[item.type]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-4 text-center text-sm text-foreground-500">
              没有找到与“{keyword}”相关的内容
            </p>
          )}
        </div>
      )}
    </div>
  );
}